import { FormEvent, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { StatCard, Modal, DataTable, useToast } from '../organisms';
import { EntityHeader } from '../shell';
import { formatDateTime } from '../lib/orgTimezone';
import { humanizeToken } from '../lib/inlineAttribution';
import { useEntityPermission } from '../lib/usePermissions';
import type { Transmittal, TransmittalStatus } from './TransmittalsList';
import { STATUS_VARIANT } from './TransmittalsList';

/**
 * TransmittalDetail — /v2/transmittals/:id
 *
 * The tracked transmittal (Domain 15 · PRD 15.3, backlog 15.3) over the REAL
 * backend (GET /api/transmittals/:id → services/transmittals.getTransmittal),
 * with its constituent tax bills, status history (audit_logs echo), and the
 * email-outbox send record. Drives the lifecycle:
 *
 *   draft ──generate──▶ generated ──send──▶ sent ──delivery──▶ delivered / failed
 *
 * Actions hit the shipped endpoints:
 *   POST /api/transmittals/:id/generate   {format?}
 *   POST /api/transmittals/:id/send
 *   POST /api/transmittals/:id/delivery   {status,reason?}   (ops reconcile of the webhook seam)
 *   GET  /api/transmittals/:id/preview?format=html           (opened in a new tab)
 *
 * The send action queues the email via the Resend outbox keystone; the send
 * record's status (queued → sent → delivered/failed, advanced by the flush job +
 * Resend delivery webhook) is surfaced live in the Delivery card below.
 */

interface TransmittalItem {
  tax_bill_id: string;
  account_number: string | null;
  tax_year: number | null;
  bill_status: string | null;
  tax_amount: number | null;
}

interface HistoryRow {
  id: string;
  entity_type: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  action: string | null;
  user_id: string | null;
  created_at: string;
}

interface SendRecord {
  id: string;
  status: string | null;
  sent_at: string | null;
  last_error: string | null;
}

interface TransmittalDetailResponse extends Transmittal {
  items: TransmittalItem[];
  history: HistoryRow[];
  send: SendRecord | null;
}

function fmtDateTime(v: string | null | undefined): string {
  return formatDateTime(v);
}

function errMessage(err: unknown): string {
  return err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
    ? String((err.body as { error: unknown }).error)
    : err instanceof Error ? err.message : String(err);
}

// Which transitions are valid from each status (mirrors services/transmittals).
const CAN_GENERATE: TransmittalStatus[] = ['draft', 'generated', 'failed'];
const CAN_SEND: TransmittalStatus[] = ['generated', 'failed'];
const CAN_DELIVER: TransmittalStatus[] = ['sent'];

export function TransmittalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  // Generate / Send / Record-delivery all mutate the transmittal (built over the
  // client's tax bills) → gate on tax_bill.can_edit. Preview is read-only, ungated.
  const canEditTransmittal = useEntityPermission('tax_bill', 'can_edit');

  const query = useQuery<TransmittalDetailResponse>({
    queryKey: ['transmittal', id],
    queryFn: ({ signal }) => api.get<TransmittalDetailResponse>(`/api/transmittals/${id}`, signal),
    enabled: !!id,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['transmittal', id] });
    queryClient.invalidateQueries({ queryKey: ['transmittals-list'] });
  }

  const generateMutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/transmittals/${id}/generate`, {}),
    onSuccess: () => { toast({ variant: 'success', message: 'Artifact generated.' }); invalidate(); },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const sendMutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/transmittals/${id}/send`, {}),
    onSuccess: () => { toast({ variant: 'success', message: 'Transmittal sent (queued via outbox).' }); invalidate(); },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading transmittal…</p>;
  }
  if (query.error) {
    const notFound = query.error instanceof ApiClientError && query.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Transmittal not found' : 'Failed to load transmittal'}</h2>
        <p style={{ margin: 0 }}>{(query.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/transmittals')}>Back to Transmittals</Button>
        </p>
      </div>
    );
  }
  if (!query.data) return null;

  const t = query.data;
  const canGenerate = CAN_GENERATE.includes(t.status);
  const canSend = CAN_SEND.includes(t.status);
  const canDeliver = CAN_DELIVER.includes(t.status);
  const canPreview = !!t.id;

  return (
    <>
      <EntityHeader
        prefix="Transmittal"
        title={t.subject || 'Tax Bill Transmittal'}
        subtitle={t.recipient_email ? `To: ${t.recipient_email}` : `Recipient: ${t.recipient_kind || 'client'}`}
        statusPills={<Badge variant={STATUS_VARIANT[t.status]} dot>{humanizeToken(t.status)}</Badge>}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/transmittals')}>Back</Button>
            {canPreview && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(`/api/transmittals/${t.id}/preview?format=html`, '_blank', 'noopener')}
              >
                Preview
              </Button>
            )}
            {canGenerate && canEditTransmittal && (
              <Button variant="secondary" size="sm" loading={generateMutation.isPending} onClick={() => generateMutation.mutate()}>
                {t.status === 'draft' ? 'Generate' : 'Re-generate'}
              </Button>
            )}
            {canSend && canEditTransmittal && (
              <Button variant="primary" size="sm" loading={sendMutation.isPending} onClick={() => sendMutation.mutate()}>
                Send
              </Button>
            )}
            {canDeliver && canEditTransmittal && (
              <Button variant="secondary" size="sm" onClick={() => setDeliveryOpen(true)}>
                Record delivery
              </Button>
            )}
          </>
        }
        kpis={
          <>
            <StatCard
              label="Status"
              value={t.status}
              caption={`${t.bill_count ?? t.items.length} bill${(t.bill_count ?? t.items.length) === 1 ? '' : 's'}`}
              accent={t.status === 'delivered' ? 'green' : t.status === 'failed' ? 'red' : 'hunter'}
            />
            <StatCard
              label="Generated"
              value={t.generated_at ? fmtDateTime(t.generated_at) : '—'}
              caption={t.artifact_format ? t.artifact_format.toUpperCase() : 'Not generated'}
              accent={t.generated_at ? 'green' : 'info'}
            />
            <StatCard
              label="Sent"
              value={t.sent_at ? fmtDateTime(t.sent_at) : '—'}
              caption={t.recipient_email || 'Not sent'}
              accent={t.sent_at ? 'green' : 'amber'}
            />
            <StatCard
              label="Delivery"
              value={t.status === 'delivered' ? 'Delivered' : t.status === 'failed' ? 'Failed' : 'Pending'}
              caption={t.delivered_at ? fmtDateTime(t.delivered_at) : (t.failed_reason || '—')}
              accent={t.status === 'delivered' ? 'green' : t.status === 'failed' ? 'red' : 'amber'}
            />
          </>
        }
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-4)',
      }}>
        <InfoCard label="Delivery">
          <Row label="Recipient kind" value={t.recipient_kind || '—'} />
          <Row label="Recipient email" value={t.recipient_email || <span style={{ color: 'var(--color-muted)' }}>resolved at send</span>} />
          <Row label="Outbox status" value={
            t.send
              ? <Badge variant={sendVariant(t.send.status)}>{t.send.status || 'queued'}</Badge>
              : <span style={{ color: 'var(--color-muted)' }}>not queued</span>
          } />
          {t.send?.last_error && <Row label="Last error" value={<span style={{ color: 'var(--color-status-red)' }}>{t.send.last_error}</span>} />}
          {t.failed_reason && <Row label="Failure reason" value={<span style={{ color: 'var(--color-status-red)' }}>{t.failed_reason}</span>} />}
        </InfoCard>
        <InfoCard label="Artifact">
          <Row label="Format" value={t.artifact_format ? t.artifact_format.toUpperCase() : '—'} />
          <Row label="Stored" value={t.r2_key ? <Badge variant="success">yes</Badge> : <Badge variant="neutral">no</Badge>} />
          <Row label="Template" value={t.template_id ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{t.template_id.slice(0, 8)}…</span> : 'Default layout'} />
        </InfoCard>
      </div>

      <SectionCard label={`Covered bills (${t.items.length})`}>
        <CoveredBillsTable items={t.items} onOpen={(billId) => navigate(`/tax-bills/${billId}`)} />
      </SectionCard>

      <SectionCard label={`History (${t.history.length})`}>
        {t.history.length === 0
          ? <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No history.</p>
          : (
            <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {t.history.map((h) => (
                <li key={h.id} style={{
                  display: 'flex',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-2) 0',
                  borderBottom: '1px solid var(--color-border)',
                  fontSize: 'var(--fs-sm)',
                }}>
                  <span style={{ color: 'var(--color-ink-3)', minWidth: 150, fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(h.created_at)}</span>
                  <span style={{ flex: 1 }}>
                    <strong>{h.action || 'update'}</strong>
                    {h.field_name && <> · {h.field_name.replace(/_/g, ' ')}</>}
                    {h.new_value != null && (
                      <>: {h.old_value != null && <span style={{ color: 'var(--color-ink-3)' }}>{h.old_value} → </span>}<strong>{h.new_value}</strong></>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
      </SectionCard>

      <RecordDeliveryModal
        open={deliveryOpen}
        onClose={() => setDeliveryOpen(false)}
        transmittalId={t.id}
        onDone={invalidate}
      />
    </>
  );
}

/**
 * The transmittal's constituent tax bills, on the canonical DataTable (matching
 * every other list/detail table in the app) instead of a bespoke raw <table>.
 * Row click → the tax-bill detail (the dominant row → detail edit affordance).
 */
function CoveredBillsTable({ items, onOpen }: { items: TransmittalItem[]; onOpen: (billId: string) => void }) {
  const columns = useMemo<ColumnDef<TransmittalItem>[]>(() => [
    {
      accessorKey: 'account_number',
      header: 'Account #',
      cell: ({ row, getValue }) => (
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--brand-primary)' }}>
          {(getValue() as string | null) || row.original.tax_bill_id.slice(0, 8)}
        </span>
      ),
    },
    { accessorKey: 'tax_year', header: 'Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    {
      accessorKey: 'bill_status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() as string | null;
        return s ? <Badge variant="neutral">{s.replace(/_/g, ' ')}</Badge> : '—';
      },
    },
    {
      accessorKey: 'tax_amount',
      header: 'Amount',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
  ], []);

  return (
    <DataTable
      data={items}
      columns={columns}
      onRowClick={(it) => onOpen(it.tax_bill_id)}
      emptyMessage="No bills."
    />
  );
}

function sendVariant(status: string | null): 'success' | 'warning' | 'neutral' | 'info' | 'danger' {
  switch (status) {
    case 'delivered':
    case 'sent': return 'success';
    case 'failed':
    case 'bounced': return 'danger';
    case 'sending': return 'info';
    default: return 'warning';
  }
}

interface RecordDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  transmittalId: string;
  onDone: () => void;
}

/**
 * Record the delivery outcome of a sent transmittal — the authenticated ops
 * reconcile of the Resend delivery-webhook seam. POST /api/transmittals/:id/delivery
 * with { status:'delivered'|'failed', reason? }; server CAS moves sent → delivered|failed.
 */
function RecordDeliveryModal({ open, onClose, transmittalId, onDone }: RecordDeliveryModalProps) {
  const { toast } = useToast();
  const [outcome, setOutcome] = useState<'delivered' | 'failed'>('delivered');
  const [reason, setReason] = useState('');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/transmittals/${transmittalId}/delivery`, {
      status: outcome,
      reason: outcome === 'failed' ? (reason.trim() || undefined) : undefined,
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: `Marked ${outcome}.` });
      onDone();
      setReason('');
      onClose();
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!mutation.isPending) mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title="Record delivery"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => !mutation.isPending && mutation.mutate()}>
            Record
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Reconcile the delivery outcome (normally driven by the Resend webhook).
        </p>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
            <input type="radio" name="outcome" checked={outcome === 'delivered'} onChange={() => setOutcome('delivered')} /> Delivered
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
            <input type="radio" name="outcome" checked={outcome === 'failed'} onChange={() => setOutcome('failed')} /> Failed
          </label>
        </div>
        {outcome === 'failed' && (
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>Reason (optional)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              style={{
                width: '100%',
                padding: 'var(--sp-2) var(--sp-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-md)',
                background: 'var(--color-card)',
                color: 'var(--color-ink)',
                fontSize: 'var(--fs-md)',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
              }}
              placeholder="bounce / spam / mailbox full"
            />
          </label>
        )}
      </form>
    </Modal>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <CardHeading>{label}</CardHeading>
      {children}
    </div>
  );
}

function SectionCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
      marginBottom: 'var(--sp-4)',
    }}>
      <CardHeading>{label}</CardHeading>
      {children}
    </div>
  );
}

function CardHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      margin: '0 0 var(--sp-3)',
      fontSize: 'var(--fs-xs)',
      fontWeight: 'var(--fw-semibold)',
      textTransform: 'uppercase',
      letterSpacing: 'var(--ls-wider)',
      color: 'var(--color-ink-3)',
    }}>{children}</h3>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0',
      fontSize: 'var(--fs-sm)',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}
